<template>
  <div class="keywords">
    <KeywordHeader :keywordCount="keywordCount" />

    <BaseLoadingBar is-small v-if="pending" />
    <div v-else-if="keywordCount === 0" class="keywords__empty">
      <LargeNotice
        v-on:click="contactUs"
        :title="$t('page.keywords.no-keywords-title')"
        :subtitle="$t('page.keywords.no-keywords-subtitle')"
        :buttonText="$t('page.keywords.contact')"
        :buttonIcon="'arrow-right'"
      />
    </div>
    <div class="keywords__main" v-else>
      <language-tab :languages="languages" :tabIndex="tabIndex" :onchange="onTabChange"></language-tab>
      <!-- Phones: the count line (docs/page-layout.md), for the language shown. -->
      <BasePhoneCountLine v-if="isPhone" class="keywords__count-line">
        <template #count>
          <span class="number-monospace">{{ languageKeywordCount }}</span>
          {{ ' ' + $tc('basic-words.keywords', languageKeywordCount) }}
        </template>
      </BasePhoneCountLine>
      <div class="keywords__main-content">
        <category :category="category" :key="category.id" v-for="category in currentCategories"></category>
      </div>
    </div>
  </div>
</template>

<script>
import { Beacon } from '@/plugins/beacon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BasePhoneCountLine from '@/components/base/BasePhoneCountLine.vue';
import Category from '@/components/page-keywords/Category';
import KeywordHeader from '@/components/page-keywords/KeywordHeader';
import LanguageTab from '@/components/page-keywords/LanguageTab';
import LargeNotice from '@/components/notice/LargeNotice';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageKeywords',
  components: {
    BaseLoadingBar,
    BasePhoneCountLine,
    Category,
    KeywordHeader,
    LanguageTab,
    LargeNotice,
  },
  data() {
    return {
      tabIndex: 0,
    };
  },
  computed: {
    ...mapGetters('keywords', ['pending', 'languages', 'keywordCount']),
    currentCategories() {
      return this.languages.length > 0 ? this.languages[this.tabIndex].categories : [];
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    // What the tab shows: the header's count, of every language, isn't shown on phones.
    languageKeywordCount() {
      const language = this.languages[this.tabIndex];
      return language ? language.keywordCount : 0;
    },
  },
  methods: {
    ...mapActions('keywords', ['getKeywords']),
    onTabChange(tabIndex) {
      this.tabIndex = tabIndex;
    },
    contactUs() {
      this.$track.contactUs('invite');
      Beacon.toggle();
      Beacon.navigate('ask');
    },
  },
  created() {
    this.getKeywords();
  },
};
</script>

<style lang="scss">
.keywords {
  &__main {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;

    &-content {
      background: $color-white;
      border-radius: $border-radius-root;
      box-shadow: $shadow-default;
      display: flex;
      gap: 32px;
      flex-wrap: wrap;
      padding: 32px;

      @include screen-below('tablet') {
        padding: $space-md;
      }
    }

    // Phones: the languages on the white panel under the header, the count line under them.
    @include screen-below('tablet') {
      padding: 0 $phone-page-gutter $phone-page-gutter;

      .language-tabs {
        @include phone-filter-panel {
          padding-top: 0;
          padding-bottom: 0;
          background: $color-white;
          box-shadow: $shadow-dropdown;
        }
      }
    }
  }

  &__count-line {
    margin-top: $space-md;
  }

  &__empty {
    margin: auto;
    display: flex;
    justify-content: center;
    height: 70vh;
    width: calc(100% - 64px);
  }

  @media screen and (max-width: 1400px) {
    &__main {
      &-content {
        grid-template-columns: 1fr 1fr;
      }
    }
  }

  @media screen and (max-width: 784px) {
    &__main {
      &-content {
        display: block;
      }
    }
  }
}

// If BaseBannerFixed is present (.has-update on App.vue) we need to add spacing
.has-update .keywords {
  padding-top: 38px;
  transition: padding 250ms ease-out;

  &__sidebar {
    min-height: calc(var(--viewport-height) - 38px);
  }
}
</style>
